import { Alert, Chip, Group, Stack, Text, TextInput } from "@mantine/core";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type { PlaceholderInfo } from "./api";
import { hasTaskIdToken, unknownPlaceholders } from "./validation";

// Имена task-id-плейсхолдеров из легенды (флаг `is_task_id`).
function taskIdNames(placeholders: readonly PlaceholderInfo[]): string[] {
  return placeholders
    .filter((placeholder) => placeholder.is_task_id)
    .map((placeholder) => placeholder.name);
}

// Все известные имена плейсхолдеров из легенды.
function knownNames(placeholders: readonly PlaceholderInfo[]): string[] {
  return placeholders.map((placeholder) => placeholder.name);
}

// Готов ли шаблон к отправке: непуст и содержит хотя бы один
// task-id-токен. Серверная валидация остаётся источником правды;
// здесь — pre-flight для блокировки кнопки «Сохранить».
export function isTemplateSubmittable(
  template: string,
  placeholders: readonly PlaceholderInfo[],
): boolean {
  const trimmed = template.trim();
  if (trimmed.length === 0) {
    return false;
  }
  return hasTaskIdToken(trimmed, taskIdNames(placeholders));
}

// Поле ввода шаблона: TextInput + ряд пресет-чипов (клик подставляет
// шаблон) + легенда + предупреждения (нет task-id / неизвестные
// плейсхолдеры) + опциональная серверная ошибка. Переиспользуется
// org- и repo-секциями.
export function TemplateField({
  value,
  onChange,
  presets,
  placeholders,
  label,
  serverError,
  legendSlot,
}: {
  readonly value: string;
  readonly onChange: (template: string) => void;
  readonly presets: readonly string[];
  readonly placeholders: readonly PlaceholderInfo[];
  readonly label: string;
  readonly serverError: string | null;
  readonly legendSlot: ReactElement;
}): ReactElement {
  const { t } = useTranslation();
  const trimmed = value.trim();
  const missingTaskId =
    trimmed.length > 0 && !hasTaskIdToken(trimmed, taskIdNames(placeholders));
  const unknown = unknownPlaceholders(trimmed, knownNames(placeholders));

  return (
    <Stack gap="sm">
      <TextInput
        label={label}
        placeholder={t("branchNameTemplate.field.placeholder")}
        value={value}
        onChange={(event) => {
          onChange(event.currentTarget.value);
        }}
      />

      <Stack gap={4}>
        <Text size="sm" fw={600}>
          {t("branchNameTemplate.presets.title")}
        </Text>
        <Group gap="xs">
          {presets.map((preset) => (
            <Chip
              key={preset}
              checked={value === preset}
              onClick={() => {
                onChange(preset);
              }}
              size="xs"
            >
              {preset}
            </Chip>
          ))}
        </Group>
      </Stack>

      {legendSlot}

      {missingTaskId ? (
        <Alert color="red" title={t("branchNameTemplate.validation.title")}>
          {t("branchNameTemplate.validation.missingTaskId")}
        </Alert>
      ) : null}

      {unknown.length > 0 ? (
        <Alert
          color="yellow"
          title={t("branchNameTemplate.validation.warnTitle")}
        >
          {t("branchNameTemplate.validation.unknown", {
            names: unknown.map((name) => `{${name}}`).join(", "),
          })}
        </Alert>
      ) : null}

      {serverError !== null ? (
        <Alert color="red" title={t("branchNameTemplate.serverError.title")}>
          {serverError}
        </Alert>
      ) : null}
    </Stack>
  );
}
